import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';

const CONTAINER = 'max-w-6xl mx-auto px-6 lg:px-8';
const H1 = 'text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-[#0B132B]';
const H2 = 'text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#0B132B]';
const EYEBROW = 'text-[11px] font-bold uppercase tracking-[0.22em] text-[#DA291C]';
const BODY = 'text-base sm:text-lg text-slate-500 leading-relaxed';

const INDUSTRIES = [
  {
    name: 'Post Offices',
    image: 'https://images.unsplash.com/photo-1758795680241-ba9cd3fa2cc2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxwb3N0JTIwb2ZmaWNlJTIwY291bnRlciUyMHdvcmtlcnxlbnwwfHx8fDE3ODU1MDgxMTZ8MA&ixlib=rb-4.1.0&q=85',
    lede: 'Built with sub-postmasters in mind.',
    body: 'Trusted, till-accountable cover for Post Office branches during illness, holiday or peak periods. Every shift has an audit trail — a must-have for cash-handling businesses.',
    bullets: ['Till checks every 3 hours', 'Smart ID verification', 'Shift records ready for HR'],
  },
  {
    name: 'Convenience Stores',
    image: 'https://images.unsplash.com/photo-1759153820384-12c9ddf8bd8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxjb252ZW5pZW5jZSUyMHN0b3JlJTIwbW9kZXJuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg1NTA4MTE2fDA&ixlib=rb-4.1.0&q=85',
    lede: 'Front-of-till cover with full accountability.',
    body: 'Reliable cover for busy convenience stores — with verified profiles, live availability and a shift audit trail. Get someone behind the counter fast, without compromise.',
    bullets: ['Same-day cover', 'Verified reliability scores', 'Multi-site friendly'],
  },
  {
    name: 'Retail',
    image: 'https://images.unsplash.com/photo-1777895610711-0a51df074e41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxyZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yJTIwbW9kZXJufGVufDB8fHx8MTc4NTUwODExNnww&ixlib=rb-4.1.0&q=85',
    lede: 'Cover the shop floor confidently.',
    body: 'Verified workers with retail experience only. Filter by category, POS familiarity or specific brand exposure to find someone who can step in and thrive from minute one.',
    bullets: ['Skill-based filtering', 'Two-way ratings', 'Save favourite workers'],
  },
  {
    name: 'Hospitality',
    image: 'https://images.unsplash.com/photo-1758526348403-b729c700d675?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxob3NwaXRhbGl0eSUyMHJlc3RhdXJhbnQlMjBzdGFmZiUyMHByZW1pdW18ZW58MHx8fHwxNzg1NTA4MTE2fDA&ixlib=rb-4.1.0&q=85',
    lede: 'Cover for baristas, waiting staff and hosts.',
    body: 'Same-day cover for hospitality teams with reliability scores you can trust. Post a shift starting in hours and find someone who already knows the ropes.',
    bullets: ['Same-day availability', 'Reliability-first matching', 'In-app messaging'],
  },
];

export default function IndustriesPage() {
  return (
    <div data-testid="industries-page">
      <section className={`${CONTAINER} pt-32 pb-16 lg:pt-40 lg:pb-20`}>
        <div className="max-w-3xl">
          <p className={EYEBROW}>Industries</p>
          <h1 className={`${H1} mt-5`}>Made for customer-facing teams.</h1>
          <p className={`${BODY} mt-6 max-w-xl`}>
            Different sectors, one shared need — trusted people behind the counter, on the shop floor, or in front of guests.
          </p>
        </div>
      </section>

      <section className={`${CONTAINER} pb-24 lg:pb-32 space-y-16 lg:space-y-24`}>
        {INDUSTRIES.map((ind, i) => (
          <div key={ind.name} className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center" data-testid={`industry-detail-${ind.name.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className={i % 2 === 1 ? 'lg:order-2' : ''}>
              <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-slate-200/70 shadow-[0_16px_40px_rgba(11,19,43,0.06)]">
                <img src={ind.image} alt={ind.name} className="h-full w-full object-cover" loading="lazy" />
              </div>
            </div>
            <div className={i % 2 === 1 ? 'lg:order-1' : ''}>
              <p className={EYEBROW}>{ind.name}</p>
              <h2 className={`${H2} mt-3`}>{ind.lede}</h2>
              <p className={`${BODY} mt-5 max-w-md`}>{ind.body}</p>
              <ul className="mt-6 space-y-2.5">
                {ind.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-[14.5px] text-slate-700">
                    <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-slate-50/70 py-20 lg:py-24">
        <div className={`${CONTAINER} text-center max-w-2xl`}>
          <h2 className={`${H2}`}>Your industry, done properly.</h2>
          <p className={`${BODY} mt-4`}>14-day free trial. No card required.</p>
          <Link to="/register?role=manager" className="mt-8 inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#DA291C] text-white font-semibold hover:bg-[#b91f13] transition-colors" data-testid="industries-cta">
            Start Free Trial <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
